import React, { memo, useCallback, useMemo, useState, lazy, Suspense } from "react"
import { AspectRatio, Box, Flex, GridItem, GridItemProps, Image, Text } from "@chakra-ui/react"

import { InView } from "react-intersection-observer"

import { useAnalytics } from "@app/hooks/useAnalytics"
import { useMedia } from "@app/hooks/useMedia"
import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import { Link } from "@app/components/Link"

import type { ElasticImage, ElasticProduct } from "@usereactify/search"
import type { ImageProps, ProductProps } from "@root/types/global"
import { useSwatch, type GroupSize, type SwatchFull } from "@app/hooks/useSwatch"

import { useGlobalContext } from "@app/providers/global"

const PLPCallout = lazy(() => import("@app/components/PLPCallout"))
const ProductCardSwatches = lazy(() => import("@app/components/Product/Card/ProductCardSwatches"))
const ProductFormPrice = lazy(() => import("@app/components/Product/Form/ProductFormPrice"))

export type LookBookProductCardProps = {
  item: ProductProps | ElasticProduct
  handleTrackingClick?: () => void
  pagePosition: number
  hasFullItem?: boolean
}

type Props = LookBookProductCardProps & GridItemProps

type Image = ImageProps | ElasticImage | undefined

export const LookBookProductCard: React.FC<Props> = memo(
  ({ item, handleTrackingClick = null, pagePosition, itemRef, hasFullItem, ...props }) => {
    const {
      settings: {
        product: { defaultCardImageSize },
      },
    } = useConfigContext()
    const {
      helpers: { isBrowser },
    } = useCore()
    const { additionalViewProductLabel } = useGlobalContext()

    const { formatActiveProduct } = useSwatch(defaultCardImageSize)
    const isReactifyData = "storefrontId" in item
    const [activeProduct, setActiveProduct] = useState<SwatchFull | null>(
      formatActiveProduct(item, null, true, isReactifyData) as SwatchFull
    )

    const { trackProductClick } = useAnalytics()
    const { isTouch } = useMedia()
    const [allSwatches, setAllSwatches] = useState([] as Array<SwatchFull>)
    const [firstAvailableVariant, setFirstAvailableVariant] = useState(
      activeProduct?.variants?.find(variant => variant.availableForSale == true) || item?.variants?.[0] || undefined
    )

    const [isHovered, setIsHovered] = useState(false)

    const primaryImage = useMemo(() => activeProduct?.images?.[0], [activeProduct?.images])

    const secondaryImage = useMemo(() => activeProduct?.images?.[1], [activeProduct?.images])

    const handleActiveProduct = useCallback((activeSwatch: SwatchFull) => {
      setActiveProduct(activeSwatch)
      setFirstAvailableVariant(activeSwatch?.variants.find(variant => variant.availableForSale == true) || activeSwatch?.variants?.[0])
    }, [])

    const handleClick = useCallback(() => {
      const productToTrack = activeProduct?.product ? { ...activeProduct.product, source: "Lookbook" } : { ...item, source: "Lookbook" }
      trackProductClick(productToTrack, firstAvailableVariant, pagePosition)
      if (handleTrackingClick) {
        handleTrackingClick()
      }
    }, [item, trackProductClick, activeProduct?.product, firstAvailableVariant, pagePosition, handleTrackingClick])

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [productSizes, setProductSizes] = useState([] as Array<GroupSize>)

    return (
      <GridItem colSpan={1} {...props} ref={itemRef} flex="1">
        <Box as={InView} threshold={0} height="100%">
          <Flex flexDirection="row" height="100%" gap="6">
            <Box
              pos="relative"
              width="100%"
              maxWidth={{
                base: "120px",
                sm: hasFullItem ? "135px" : "186px",
              }}
            >
              {/* Workaround to prevent link hover to trigger un-necessary re-rerenders, crashing local dev */}
              <div
                onMouseEnter={e => {
                  e.stopPropagation()
                  setIsHovered(true)
                }}
                onMouseLeave={() => setIsHovered(false)}
                style={{ height: "100%" }}
              >
                <Link
                  to={activeProduct?.url || ""}
                  title={activeProduct?.title || item?.title}
                  role={!isTouch ? "group" : undefined}
                  onClick={handleClick}
                  display="block"
                  w="full"
                  height="100%"
                >
                  <AspectRatio bg="grey.cloud" ratio={320 / 450} w="full" height="100%">
                    <>
                      {primaryImage && <Image src={primaryImage?.src} alt={primaryImage?.alt || item.title} loading="lazy" />}
                      {secondaryImage && isBrowser && isHovered && (
                        <Image
                          src={secondaryImage?.src}
                          alt={secondaryImage?.alt || item.title}
                          transition="all 0.2s ease"
                          opacity={0}
                          _groupHover={{ opacity: 1 }}
                          loading="lazy"
                        />
                      )}
                    </>
                  </AspectRatio>
                </Link>
              </div>
            </Box>
            <Flex flexDir="column" justifyContent="space-between">
              <Box>
                <Box
                  onMouseEnter={e => {
                    e.stopPropagation()
                    setIsHovered(true)
                  }}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <Link
                    to={activeProduct?.url || ""}
                    role={!isTouch ? "group" : undefined}
                    title={activeProduct?.product?.title || ""}
                    onClick={handleClick}
                    display="block"
                    w="full"
                  >
                    <Box flexGrow={1}>
                      <Text size="md" lineHeight="6" color="grey.coal">
                        {activeProduct?.product?.title || ""}
                      </Text>
                    </Box>
                    {isBrowser && activeProduct && (
                      <Suspense fallback={<></>}>
                        <Box flexShrink={0}>
                          <ProductFormPrice
                            variant={firstAvailableVariant}
                            direction="row"
                            alignItems="flex-start"
                            spacing={0}
                            size="sm"
                            lineHeight="6"
                          />
                        </Box>
                      </Suspense>
                    )}
                  </Link>
                </Box>
                {isBrowser && (
                  <Suspense fallback={<></>}>
                    <PLPCallout tags={activeProduct?.product?.tags || []} fontSize={12} letterSpacing="generous" />
                  </Suspense>
                )}

                {isBrowser && activeProduct && (
                  <Suspense fallback={<></>}>
                    <Box marginTop="2">
                      <ProductCardSwatches
                        activeProduct={activeProduct}
                        allSwatches={allSwatches}
                        item={item}
                        setAllSwatches={setAllSwatches}
                        setProductSizes={setProductSizes}
                        setActiveProduct={handleActiveProduct}
                        mt={{ base: 0.5, md: 1 }}
                      />
                    </Box>
                  </Suspense>
                )}
              </Box>
              <Flex>
                <Link
                  to={activeProduct?.url || ""}
                  title={activeProduct?.title || item?.title}
                  onClick={handleClick}
                  variant="underline"
                  color="grey.lake"
                  lineHeight={1.5}
                  fontSize={{ base: 12.66, md: 14 }}
                  mb="2"
                  borderBottomColor="grey.lake"
                >
                  {additionalViewProductLabel}
                </Link>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </GridItem>
    )
  }
)
